import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform } from 'react-native';
import { useColorScheme as useNativeWindColorScheme } from 'nativewind';
import * as SecureStore from 'expo-secure-store';

export type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  preference: ThemePreference;
  setPreference: (pref: ThemePreference) => void;
  /** The resolved scheme after applying system preference */
  resolvedScheme: 'light' | 'dark';
}

const STORAGE_KEY = 'perdiem-theme-preference';

const ThemeContext = createContext<ThemeContextValue>({
  preference: 'system',
  setPreference: () => {},
  resolvedScheme: 'light',
});

export function useTheme() {
  return useContext(ThemeContext);
}

async function loadPreference(): Promise<ThemePreference> {
  try {
    if (Platform.OS === 'web') {
      if (typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function') {
        const val = localStorage.getItem(STORAGE_KEY);
        if (val === 'light' || val === 'dark' || val === 'system') return val;
      }
      return 'system';
    }
    const val = await SecureStore.getItemAsync(STORAGE_KEY);
    if (val === 'light' || val === 'dark' || val === 'system') return val;
    return 'system';
  } catch {
    return 'system';
  }
}

function savePreference(pref: ThemePreference) {
  try {
    if (Platform.OS === 'web') {
      if (typeof localStorage !== 'undefined' && typeof localStorage.setItem === 'function') {
        localStorage.setItem(STORAGE_KEY, pref);
      }
      return;
    }
    SecureStore.setItemAsync(STORAGE_KEY, pref).catch(() => {});
  } catch {
    // Ignore storage failures
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { colorScheme, setColorScheme } = useNativeWindColorScheme();
  const [preference, setPreferenceState] = useState<ThemePreference>('system');
  const [loaded, setLoaded] = useState(false);

  // Load persisted preference and apply it to NativeWind
  useEffect(() => {
    loadPreference().then((pref) => {
      setPreferenceState(pref);
      setColorScheme(pref);
    }).finally(() => setLoaded(true));
  }, [setColorScheme]);

  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    savePreference(pref);
    setColorScheme(pref);
  }, [setColorScheme]);

  const resolvedScheme: 'light' | 'dark' = colorScheme === 'dark' ? 'dark' : 'light';

  // Apply dark class to html element on web for CSS custom properties
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const root = document.documentElement;
    if (resolvedScheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedScheme]);

  const value = useMemo(
    () => ({ preference, setPreference, resolvedScheme }),
    [preference, setPreference, resolvedScheme],
  );

  if (!loaded) return null;

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
